import { useForm } from '@inertiajs/react';
import { Pencil } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toastFlash } from '@/lib/flash-toast';
import type { ExternalPerson } from '@/types';

interface EditExternalPersonDialogProps {
  eventSlug: string;
  person: ExternalPerson;
}

export function EditExternalPersonDialog({
  eventSlug,
  person,
}: EditExternalPersonDialogProps) {
  const [open, setOpen] = useState(false);
  const form = useForm({
    slug: person.slug,
    fullName: person.fullName,
    category: person.category,
    role: person.role ?? '',
    githubUrl: person.githubUrl ?? '',
    linkedinUrl: person.linkedinUrl ?? '',
    redirectUrl: person.redirectUrl ?? '',
    marketEnabled: person.marketEnabled,
  });
  const { data, setData, processing } = form;
  const errors = form.errors as unknown as Record<string, string | undefined>;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    form.patch(`/admin/${eventSlug}/external-people/${person.id}`, {
      preserveScroll: true,
      onSuccess: (page) => {
        toastFlash(page, 'External person updated successfully!');
        setOpen(false);
      },
    });
  };

  return (
    <>
      <Button variant="ghost" size="icon" onClick={() => setOpen(true)}>
        <Pencil className="h-4 w-4" />
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Edit External Person</DialogTitle>
            <DialogDescription>
              Update information for {person.fullName}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid gap-2">
              <Label htmlFor={`edit-external-person-slug-${person.id}`}>
                Slug
              </Label>
              <Input
                id={`edit-external-person-slug-${person.id}`}
                placeholder="john-doe"
                disabled={processing}
                value={data.slug}
                onChange={(e) => setData('slug', e.target.value)}
              />
              {errors.slug && (
                <p className="text-destructive text-sm">{errors.slug}</p>
              )}
            </div>

            <div className="grid gap-2">
              <Label htmlFor={`edit-external-person-full-name-${person.id}`}>
                Full Name
              </Label>
              <Input
                id={`edit-external-person-full-name-${person.id}`}
                placeholder="John Doe"
                disabled={processing}
                value={data.fullName}
                onChange={(e) => setData('fullName', e.target.value)}
              />
              {errors.fullName && (
                <p className="text-destructive text-sm">{errors.fullName}</p>
              )}
            </div>

            <div className="grid gap-2">
              <Label htmlFor={`edit-external-person-category-${person.id}`}>
                Category
              </Label>
              <Input
                id={`edit-external-person-category-${person.id}`}
                placeholder="mentor, sponsor, judge..."
                disabled={processing}
                value={data.category}
                onChange={(e) => setData('category', e.target.value)}
              />
              {errors.category && (
                <p className="text-destructive text-sm">{errors.category}</p>
              )}
            </div>

            <div className="grid gap-2">
              <Label htmlFor={`edit-external-person-role-${person.id}`}>
                Role (Optional)
              </Label>
              <Input
                id={`edit-external-person-role-${person.id}`}
                placeholder="CTO at Company"
                disabled={processing}
                value={data.role}
                onChange={(e) => setData('role', e.target.value)}
              />
              {errors.role && (
                <p className="text-destructive text-sm">{errors.role}</p>
              )}
            </div>

            <div className="grid gap-2">
              <Label htmlFor={`edit-external-person-github-url-${person.id}`}>
                GitHub URL (Optional)
              </Label>
              <Input
                id={`edit-external-person-github-url-${person.id}`}
                placeholder="https://github.com/username"
                disabled={processing}
                value={data.githubUrl}
                onChange={(e) => setData('githubUrl', e.target.value)}
              />
              {errors.githubUrl && (
                <p className="text-destructive text-sm">{errors.githubUrl}</p>
              )}
            </div>

            <div className="grid gap-2">
              <Label htmlFor={`edit-external-person-linkedin-url-${person.id}`}>
                LinkedIn URL (Optional)
              </Label>
              <Input
                id={`edit-external-person-linkedin-url-${person.id}`}
                placeholder="https://linkedin.com/in/username"
                disabled={processing}
                value={data.linkedinUrl}
                onChange={(e) => setData('linkedinUrl', e.target.value)}
              />
              {errors.linkedinUrl && (
                <p className="text-destructive text-sm">{errors.linkedinUrl}</p>
              )}
            </div>

            <div className="grid gap-2">
              <Label htmlFor={`edit-external-person-redirect-url-${person.id}`}>
                Redirect URL (Optional)
              </Label>
              <Input
                id={`edit-external-person-redirect-url-${person.id}`}
                placeholder="https://example.com/profile"
                disabled={processing}
                value={data.redirectUrl}
                onChange={(e) => setData('redirectUrl', e.target.value)}
              />
              {errors.redirectUrl && (
                <p className="text-destructive text-sm">{errors.redirectUrl}</p>
              )}
            </div>

            <div className="flex items-center justify-between rounded-md border p-3">
              <div className="flex flex-col gap-0.5">
                <Label
                  htmlFor={`edit-external-person-market-enabled-${person.id}`}
                >
                  Banana Market access
                </Label>
                <p className="text-muted-foreground text-xs">
                  Let this person trade in the prediction market via their
                  GitHub login.
                </p>
              </div>
              <Switch
                id={`edit-external-person-market-enabled-${person.id}`}
                checked={data.marketEnabled}
                onCheckedChange={(checked) => setData('marketEnabled', checked)}
                disabled={processing}
              />
            </div>

            {errors.base && (
              <div className="rounded-md border border-red-200 bg-red-50 p-4">
                <p className="text-red-600 text-sm">{errors.base}</p>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpen(false)}
                disabled={processing}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={processing}>
                {processing ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
